/**
 * A data table (DESIGN_LANGUAGE.md §6.1, DL-245).
 *
 * Semantic `<table>` first: a screen reader gets the column headers with each
 * cell, which a grid of `<div>`s never gives. Sorting is optional and
 * client-side over the rows the caller passed — a cursor-paged list sorts
 * the page it has, and says so through `sortNote`, rather than pretending to
 * sort the collection. Below `--bp-md` the rows stack: each cell carries its
 * header as a `data-label` the stylesheet prints before it, so nothing is
 * removed to fit (§4.3, "content is never removed to fit a small screen").
 *
 * Not built here, deliberately: sticky headers, column visibility, selection
 * and virtualisation. Nothing in the observability screens has a list long
 * enough to need them; they are added when a screen does.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { cx } from '~/lib/cx';

import { ChevronDown, ChevronUpDown } from './icons';
import styles from './Table.module.css';

export interface TableColumn<T> {
  /** A stable key, used as the React key and for `data-label`. */
  key: string;
  header: ReactNode;
  /** The cell. */
  render: (row: T) => ReactNode;
  /** When present, the header is a button that sorts by this value. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'start' | 'end';
  /** Tabular figures, for a column of numbers that should line up. */
  numeric?: boolean;
  /** Hidden visually below `--bp-md`? Never: `stackLabel` names it instead. */
  stackLabel?: string;
}

export interface TableSort {
  key: string;
  direction: 'asc' | 'desc';
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /**
   * The table's accessible name. One of `caption` (visible) or `aria-label`
   * is always needed.
   */
  caption?: ReactNode;
  'aria-label'?: string;
  /** Controlled sort; leave out for internal sort state. */
  sort?: TableSort;
  onSortChange?: (sort: TableSort | undefined) => void;
  /** Shown under the table when a sort is active: "Sorted within this page". */
  sortNote?: ReactNode;
  /** A row's own class, for a muted or highlighted row. */
  rowClassName?: (row: T) => string | undefined;
  /** Dense rows, for a register that is scanned rather than read. */
  compact?: boolean;
  className?: string;
}

function compare(a: string | number | null | undefined, b: typeof a): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  caption,
  sort,
  onSortChange,
  sortNote,
  rowClassName,
  compact = false,
  className,
  ...rest
}: TableProps<T>) {
  const [internalSort, setInternalSort] = useState<TableSort | undefined>(undefined);
  const active = sort ?? internalSort;
  const setSort = onSortChange ?? setInternalSort;

  const sorted = useMemo(() => {
    if (!active) return rows;
    const column = columns.find((c) => c.key === active.key);
    if (!column?.sortValue) return rows;
    const value = column.sortValue;
    const sign = active.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => sign * compare(value(a), value(b)));
  }, [rows, columns, active]);

  const toggle = (key: string) => {
    if (active?.key !== key) {
      setSort({ key, direction: 'asc' });
    } else if (active.direction === 'asc') {
      setSort({ key, direction: 'desc' });
    } else {
      setSort(undefined);
    }
  };

  return (
    <div className={cx(styles.wrap, className)}>
      <table className={cx(styles.table, compact && styles.compact)} {...rest}>
        {caption ? <caption className={styles.caption}>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => {
              const sortable = Boolean(column.sortValue);
              const isActive = active?.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={cx(
                    styles.th,
                    column.align === 'end' && styles.end,
                    column.numeric && styles.numeric,
                  )}
                  {...(sortable && isActive
                    ? { 'aria-sort': active.direction === 'asc' ? 'ascending' : 'descending' }
                    : {})}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={cx(styles.sortButton, isActive && styles.sortActive)}
                      onClick={() => {
                        toggle(column.key);
                      }}
                    >
                      <span>{column.header}</span>
                      {isActive ? (
                        <ChevronDown
                          size={12}
                          className={cx(
                            styles.sortGlyph,
                            active.direction === 'asc' && styles.sortAsc,
                          )}
                        />
                      ) : (
                        <ChevronUpDown size={12} className={styles.sortGlyph} />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)} className={cx(styles.tr, rowClassName?.(row))}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cx(
                    styles.td,
                    column.align === 'end' && styles.end,
                    column.numeric && styles.numeric,
                  )}
                  data-label={
                    column.stackLabel ?? (typeof column.header === 'string' ? column.header : '')
                  }
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {active && sortNote ? <p className={styles.note}>{sortNote}</p> : null}
    </div>
  );
}
